import {
  PayrollRowExportType,
  ProcessedPayrollFileData,
  ValidationReport,
} from '@api/lib/payrollProcessing/types';
import {format} from 'date-fns';
import {createCsv} from '@common-web/lib/createCsv';

export function downloadCsvContent(csvContent: string, fileName: string) {
  const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
  triggerDownload(blob, fileName);
}

export function downloadXlsxBase64(xlsxBase64: string, fileName: string) {
  const byteCharacters = atob(xlsxBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  triggerDownload(blob, fileName);
}

export function exportFileValidationErrorReportCsv(validationReport: ValidationReport) {
  const errors = validationReport.rowValidation?.errors || [];
  if (errors.length === 0) return;

  const rows = errors.map((e) => ({
    'Error Level': e.level,
    'Error Type': e.type.replace(/_/g, ' '),
    'Row Number': String(e.rowNumber ?? ''),
    'Field':
      typeof e.fieldName === 'string'
        ? e.fieldName.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
        : '',
    'Value': String(e.value ?? ''),
    'Message': String(e.message ?? ''),
    'First Name': e.rowContext?.FIRST_NAME || '',
    'Last Name': e.rowContext?.LAST_NAME || '',
    'Employee ID': e.rowContext?.EMPLOYEE_ID || '',
  }));

  const csv = createCsv(rows);
  const blob = new Blob(['\ufeff', csv], {type: 'text/csv;charset=utf-8;'});
  triggerDownload(blob, `row_issues_${format(new Date(), 'yyyy_MM_dd')}.csv`);
}

export function exportFileValidationTransformedRowCsv(
  rows: ProcessedPayrollFileData['rows'],
  rowExportType: PayrollRowExportType,
) {
  if (rows.length === 0) return;

  const allKeys = Array.from(
    (rows as Record<string, unknown>[]).reduce((set, row) => {
      Object.keys(row || {}).forEach((k) => set.add(k));
      return set;
    }, new Set<string>()),
  ).sort((a, b) => a.localeCompare(b));

  const csv = createCsv(rows as Record<string, unknown>[], allKeys);
  const blob = new Blob(['\ufeff', csv], {type: 'text/csv;charset=utf-8;'});
  triggerDownload(
    blob,
    `transformed_upload_${rowExportType.toLowerCase()}_${format(new Date(), 'yyyy_MM_dd')}.csv`,
  );
}

export function exportColumnsAsCsv(columns: string[], originalFileName: string) {
  const header =
    columns.map((col) => col.replace(/"/g, '""').replace(/[\r\n]+/g, ' ')).join(',') + '\n';
  const blob = new Blob(['\ufeff', header], {type: 'text/csv;charset=utf-8;'});
  triggerDownload(blob, `${originalFileName.replace(/\.[^.]+$/, '')}_columns.csv`);
}

export function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
